"use client";

import { c as _c } from "react/compiler-runtime";
import { useContext, useState } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import { useMutation } from "@tanstack/react-query";
import type { ApiWaveSubscriptionActions } from "@/generated/models/ApiWaveSubscriptionActions";
import { commonApiDeleteWithBody, commonApiPost } from "@/services/api/common-api";
import { useAuth } from "@/components/auth/Auth";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import CircleLoader, { CircleLoaderSize } from "@/components/distribution-plan-tool/common/CircleLoader";
import { WAVE_DEFAULT_SUBSCRIPTION_ACTIONS } from "@/components/react-query-wrapper/utils/query-utils";
enum WaveFollowBtnSize {
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
}
const BUTTON_CLASSES: Record<WaveFollowBtnSize, string> = {
  [WaveFollowBtnSize.SMALL]: "tw-gap-x-1 tw-px-2.5 tw-py-2 tw-text-xs",
  [WaveFollowBtnSize.MEDIUM]: "tw-gap-x-2 tw-px-3.5 tw-py-2 tw-text-sm"
};
const SVG_CLASSES: Record<WaveFollowBtnSize, string> = {
  [WaveFollowBtnSize.SMALL]: "tw-h-4 tw-w-4",
  [WaveFollowBtnSize.MEDIUM]: "tw-h-5 tw-w-5"
};
const LOADER_SIZES: Record<WaveFollowBtnSize, CircleLoaderSize> = {
  [WaveFollowBtnSize.SMALL]: CircleLoaderSize.SMALL,
  [WaveFollowBtnSize.MEDIUM]: CircleLoaderSize.MEDIUM
};
interface WaveHeaderFollowProps {
  readonly wave: ApiWave;
  readonly subscribeToAllDrops?: boolean | undefined;
  readonly size?: WaveFollowBtnSize | undefined;
  readonly fullWidth?: boolean | undefined;
}
export default function WaveHeaderFollow(t0) {
  const $ = _c(47);
  const {
    wave,
    subscribeToAllDrops: t1,
    size: t2,
    fullWidth: t3
  } = t0;
  const subscribeToAllDrops = t1 === undefined ? false : t1;
  const size = t2 === undefined ? WaveFollowBtnSize.MEDIUM : t2;
  const fullWidth = t3 === undefined ? false : t3;
  const {
    setToast,
    requestAuth
  } = useAuth();
  const {
    onWaveFollowChange
  } = useContext(ReactQueryWrapperContext);
  const following = !!wave.subscribed_actions.length;
  const label = following ? "Joined" : "Join";
  const [mutating, setMutating] = useState(false);
  let t4;
  if ($[0] !== subscribeToAllDrops || $[1] !== wave.id) {
    t4 = async () => {
      await commonApiPost({
        endpoint: `waves/${wave.id}/subscriptions`,
        body: {
          actions: WAVE_DEFAULT_SUBSCRIPTION_ACTIONS
        }
      });
      if (subscribeToAllDrops) {
        await commonApiPost({
          endpoint: `notifications/wave-subscription/${wave.id}`,
          body: {}
        });
      }
    };
    $[0] = subscribeToAllDrops;
    $[1] = wave.id;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== onWaveFollowChange || $[4] !== wave.id) {
    t5 = () => {
      onWaveFollowChange({
        waveId: wave.id,
        following: true
      });
    };
    $[3] = onWaveFollowChange;
    $[4] = wave.id;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== setToast) {
    t6 = error => {
      setToast({
        message: error as unknown as string,
        type: "error"
      });
    };
    $[6] = setToast;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setMutating(false);
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t4 || $[10] !== t5 || $[11] !== t6) {
    t8 = {
      mutationFn: t4,
      onSuccess: t5,
      onError: t6,
      onSettled: t7
    };
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const followMutation = useMutation(t8);
  let t9;
  if ($[13] !== wave.id) {
    t9 = async () => {
      await commonApiDeleteWithBody({
        endpoint: `waves/${wave.id}/subscriptions`,
        body: {
          actions: WAVE_DEFAULT_SUBSCRIPTION_ACTIONS
        }
      });
    };
    $[13] = wave.id;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== onWaveFollowChange || $[16] !== wave.id) {
    t10 = () => {
      onWaveFollowChange({
        waveId: wave.id,
        following: false
      });
    };
    $[15] = onWaveFollowChange;
    $[16] = wave.id;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== setToast) {
    t11 = error_0 => {
      setToast({
        message: error_0 as unknown as string,
        type: "error"
      });
    };
    $[18] = setToast;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      setMutating(false);
    };
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t10 || $[22] !== t11 || $[23] !== t9) {
    t13 = {
      mutationFn: t9,
      onSuccess: t10,
      onError: t11,
      onSettled: t12
    };
    $[21] = t10;
    $[22] = t11;
    $[23] = t9;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const unFollowMutation = useMutation(t13);
  let t14;
  if ($[25] !== followMutation || $[26] !== following || $[27] !== requestAuth || $[28] !== unFollowMutation) {
    t14 = async () => {
      setMutating(true);
      const {
        success
      } = await requestAuth();
      if (!success) {
        setMutating(false);
        return;
      }
      if (following) {
        await unFollowMutation.mutateAsync();
        return;
      }
      await followMutation.mutateAsync();
    };
    $[25] = followMutation;
    $[26] = following;
    $[27] = requestAuth;
    $[28] = unFollowMutation;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const onFollow = t14;
  let t15;
  if ($[30] !== following || $[31] !== mutating || $[32] !== size) {
    t15 = () => {
      if (mutating) {
        return <CircleLoader size={LOADER_SIZES[size]} />;
      }
      if (following) {
        return <svg className="-tw-ml-1 tw-h-3 tw-w-3 tw-flex-shrink-0" viewBox="0 0 17 15" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M14.7953 0.853403L5.24867 10.0667L2.71534 7.36007C2.24867 6.92007 1.51534 6.8934 0.982005 7.26674C0.462005 7.6534 0.315338 8.3334 0.635338 8.88007L3.63534 13.7601C3.92867 14.2134 4.43534 14.4934 5.00867 14.4934C5.55534 14.4934 6.07534 14.2134 6.36867 13.7601C6.84867 13.1334 16.0087 2.2134 16.0087 2.2134C17.2087 0.986737 15.7553 -0.093263 14.7953 0.84007V0.853403Z" fill="currentColor" /></svg>;
      }
      return <svg className={`${SVG_CLASSES[size]} -tw-ml-1 tw-flex-shrink-0`} viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    };
    $[30] = following;
    $[31] = mutating;
    $[32] = size;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const printIcon = t15;
  const t16 = `tw-flex tw-items-center ${fullWidth ? "tw-w-full" : ""}`;
  const t17 = `${BUTTON_CLASSES[size]} ${following ? "tw-bg-iron-800 tw-text-iron-300 tw-ring-iron-700 hover:tw-bg-iron-700 hover:tw-ring-iron-700" : "tw-bg-primary-500 tw-text-white tw-ring-primary-500 hover:tw-bg-primary-600 hover:tw-ring-primary-600"} ${fullWidth ? "tw-h-10 tw-w-full tw-justify-center lg:tw-h-9" : ""} tw-flex tw-cursor-pointer tw-items-center tw-rounded-lg tw-border-0 tw-font-semibold tw-ring-1 tw-ring-inset tw-transition tw-duration-300 tw-ease-out`;
  let t18;
  if ($[34] !== printIcon) {
    t18 = printIcon();
    $[34] = printIcon;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== label) {
    t19 = <span>{label}</span>;
    $[36] = label;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== mutating || $[39] !== onFollow || $[40] !== t17 || $[41] !== t18 || $[42] !== t19) {
    t20 = <button onClick={onFollow} disabled={mutating} type="button" className={t17}>{t18}{t19}</button>;
    $[38] = mutating;
    $[39] = onFollow;
    $[40] = t17;
    $[41] = t18;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t16 || $[45] !== t20) {
    t21 = <div className={t16}>{t20}</div>;
    $[44] = t16;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  return t21;
}